import { Component, Input, OnInit } from '@angular/core';
import { version } from '../../../environments/version';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Web3Service } from '@/services/web3.service';
import { interval, Observable, combineLatest, map, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HeaderIconsComponent } from '../header-icons/header-icons.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { StateService } from '@/services/state.service';
import { DataService } from '@/services/data.service';
import { Auction } from '@/models/graph';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { TimerComponent } from '../auction-house/timer/timer.component';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { WeiPipe } from '@/pipes/auction-house/wei.pipe';
import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { WrapperV1Component } from '../wrapper/wrapper.v1.component';
import { WrapperV2Component } from '../wrapper/wrapper.v2.component';
import { UsdPipe } from '@/pipes/usd.pipe';
import { MyBidsComponent } from '../my-bids/my-bids.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LazyLoadImageModule,
    WrapperV1Component,
    WrapperV2Component,
    TokenIdParsePipe,
    WeiPipe,
    TimerComponent,
    WalletAddressDirective,
    HeaderIconsComponent,
    WeiToEthPipe,
    UsdPipe,
    MyBidsComponent
  ],
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss']
})
export class SideMenuComponent implements OnInit {

  isMobile$ = this.breakpointObserver
    .observe([Breakpoints.Handset, Breakpoints.Tablet])
    .pipe(
      map(result => result.matches),
      shareReplay({ refCount: true, bufferSize: 1 })
    );  

  @Input() menuState!: boolean;
  gasPrice: string | null = null;
  chain = environment.chainId;
  environment = environment;
  currentAuction: Auction | null = null;
  parseInt = parseInt;
  phunkTreasuryETHBalance: string | null = null;
  phunkTreasuryPhunkBalance: number | null = null;
  wbtcTreasuryBalance: string | null = null;
  ethTreasuryBalance: string | null = null;
  totalTreasuryUSD: number | null = null;
  version = version;
  readonly showMyBidsTab =
    environment.featureFlags?.STASH_BID_SUPPORT !== false ||
    (environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false);

  activeTab: 'wrapping-tools' | 'my-bids' = this.showMyBidsTab ? 'my-bids' : 'wrapping-tools';
  blockDelay$!: Observable<number>;
  
  constructor(protected web3Service: Web3Service,
    public stateSvc: StateService,
    protected dataSvc: DataService,
    private breakpointObserver: BreakpointObserver,
    private router: Router
  ) {}

  async ngOnInit() {
    console.log('[SideMenuComponent] ngOnInit called (SSR test)');
    
    if (!this.showMyBidsTab) {
      this.stateSvc.setSideMenuActiveTab('wrapping-tools');
    }
    
    // Subscribe to activeTab changes from StateService
    this.stateSvc.sideMenuActiveTab$.subscribe(tab => {
      this.activeTab = tab;
    });
    
    this.dataSvc.fetchCurrentAuction().subscribe(a => {
      this.currentAuction = a;
      //console.log('[SideMenuComponent] fetchCurrentAuction result:', a);
    });
    
    // Re-enable Web3 calls
    if (environment.collection === 'cryptophunks') {
      this.phunkTreasuryETHBalance = await this.web3Service.getBalance('0x61f874551c69f0E40c9f55219107B408C989aDEc');
      console.log('[SideMenuComponent] getBalance result:', this.phunkTreasuryETHBalance);
      
      this.phunkTreasuryPhunkBalance = await this.web3Service.getNftBalance('0xf07468ead8cf26c752c676e43c814fee9c8cf402', '0x61f874551c69f0E40c9f55219107B408C989aDEc');
      console.log('[SideMenuComponent] getBalance result:', this.phunkTreasuryPhunkBalance);
      
      // Get WBTC balance for 0x61f874551c69f0E40c9f55219107B408C989aDEc
      this.wbtcTreasuryBalance = await this.web3Service.getERC20Balance('0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', '0x61f874551c69f0E40c9f55219107B408C989aDEc', 8);
      console.log('[SideMenuComponent] WBTC balance result:', this.wbtcTreasuryBalance);
      
      // Get ETH balance for 0x86b525ab8c5c9b8852f3a1bc79376335bcd2f962
      this.ethTreasuryBalance = await this.web3Service.getBalance('0x86b525ab8c5c9b8852f3a1bc79376335bcd2f962');
      console.log('[SideMenuComponent] ETH balance result:', this.ethTreasuryBalance);

      // Calculate total treasury value in USD
      this.calculateTotalTreasuryUSD();
    }
    
    // Initialize blockDelay$ once to avoid recreating Observable on every change detection cycle
    this.blockDelay$ = combineLatest([
      this.web3Service.currentBlock$, 
      this.web3Service.lastIndexedBlock$
    ]).pipe(
      map(([current, indexed]) => current - indexed),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
    
    // Other calls remain disabled
    // TODO SSR
    interval(15000).subscribe(() => {
      this.updateGasPrice();
    });
    this.updateGasPrice();
  }

  toggleMenu(): void {
    this.stateSvc.setMenuOpen(!this.stateSvc.getMenuOpen());
  }

  navigateToStatus(): void {
    this.router.navigate(['/status']);
  }

  private async updateGasPrice() {
    this.gasPrice = await this.web3Service.getGasPrice();
  }

  private calculateTotalTreasuryUSD() {
    if (!this.phunkTreasuryETHBalance || !this.wbtcTreasuryBalance || !this.ethTreasuryBalance) {
      this.totalTreasuryUSD = null;
      return;
    }

    try {
      const ethPrice = this.dataSvc.getUSDPrice();
      const btcPrice = this.dataSvc.getBTCPrice();

      // Convert balances to numbers (they're in wei)
      const ethBalance1 = parseFloat(this.phunkTreasuryETHBalance); // ETH treasury
      const ethBalance2 = parseFloat(this.ethTreasuryBalance); // ETH flywheel
      const wbtcBalance = parseFloat(this.wbtcTreasuryBalance); // WBTC balance (8 decimals)

      // Calculate total USD value
      const ethTotalUSD = (ethBalance1 + ethBalance2) * ethPrice;
      const wbtcTotalUSD = wbtcBalance * btcPrice;

      this.totalTreasuryUSD = ethTotalUSD + wbtcTotalUSD;
      console.log('[SideMenuComponent] Total treasury USD:', this.totalTreasuryUSD);
      console.log('[SideMenuComponent] ETH price:', ethPrice, 'BTC price:', btcPrice);
      console.log('[SideMenuComponent] ETH balances:', ethBalance1, ethBalance2, 'WBTC balance:', wbtcBalance);
    } catch (error) {
      console.error('[SideMenuComponent] Error calculating total treasury USD:', error);
      this.totalTreasuryUSD = null;
    }
  }

}
